import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Devices as DevicesIcon,
  SimCard as SimCardIcon,
  People as PeopleIcon,
  Assignment as ContractsIcon,
  Schedule as ScheduleIcon,
  Autorenew as RenewalIcon,
  AdminPanelSettings as AdminIcon,
  Map as MapIcon,
  Campaign as CampaignIcon,
  Assessment as ReportsIcon,
  Build as MaintenanceIcon,
  Engineering as TechnicianIcon,
  Payment as PaymentIcon,
  FlashOn as QuickAssignIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AccountBalanceWallet as WalletIcon,
  Security as SecurityIcon,
  CreditCard as CreditCardIcon,
  Receipt as ReceiptIcon,
  BatchPrediction as BulkIcon,
  SmartToy as BotIcon,
  Route as RouteIcon,
  MenuBook as KnowledgeIcon,
  Psychology as InsightsIcon,
  WhatsApp as WhatsAppIcon,
  ContactPhone as LeadsIcon,
  MonitorHeart as MonitorIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const DRAWER_WIDTH = 280;
const DRAWER_WIDTH_COLLAPSED = 72;

interface SidebarProps {
  open: boolean;
  collapsed: boolean;
  onClose: () => void;
  onToggleCollapse: () => void;
  isMobile?: boolean;
}

interface MenuItem {
  title: string;
  path: string;
  icon: React.ReactNode;
  adminOnly?: boolean;
  badge?: string | number;
}

export const Sidebar: React.FC<SidebarProps> = ({
  open,
  collapsed,
  onClose,
  onToggleCollapse,
  isMobile = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { isAdmin, isAssistant, isTechnician } = useAuth();

  const technicianMenuItems: MenuItem[] = [
    {
      title: 'Mis Citas',
      path: '/technician/dashboard',
      icon: <DashboardIcon />,
    },
    {
      title: 'Calendario',
      path: '/technician/calendar',
      icon: <ScheduleIcon />,
    },
    {
      title: 'Mis Clientes',
      path: '/technician/clients',
      icon: <PeopleIcon />,
    },
    {
      title: 'Mis Finanzas',
      path: '/technician/finances',
      icon: <WalletIcon />,
    },
  ];

  const menuItems: MenuItem[] = [
    {
      title: 'Dashboard',
      path: isAdmin ? '/admin/dashboard' : '/assistant/dashboard',
      icon: <DashboardIcon />,
    },
    {
      title: 'Clientes',
      path: '/clients',
      icon: <PeopleIcon />,
    },
    {
      title: 'Dispositivos GPS',
      path: isAssistant ? '/assistant/devices' : '/devices',
      icon: <DevicesIcon />,
    },
    {
      title: 'Asignación Rápida',
      path: '/assistant/quick-assign',
      icon: <QuickAssignIcon />,
    },
    {
      title: 'Mantenimientos',
      path: '/devices/revisions',
      icon: <MaintenanceIcon />,
    },
    {
      title: 'SIM Cards',
      path: '/simcards',
      icon: <SimCardIcon />,
    },
    {
      title: 'Renovaciones',
      path: '/renewals',
      icon: <RenewalIcon />,
      badge: 'pending',
    },
    {
      title: 'Calendario',
      path: '/calendar',
      icon: <ScheduleIcon />,
    },
    {
      title: 'Mapa en Vivo',
      path: '/live-map',
      icon: <MapIcon />,
    },
    {
      title: 'Gastos',
      path: '/expenses',
      icon: <ReceiptIcon />,
    },
  ];

  // Menú principal de ADMIN, reorganizado con hubs (unifica secciones relacionadas)
  const adminMainItems: MenuItem[] = [
    {
      title: 'Dashboard',
      path: '/admin/dashboard',
      icon: <DashboardIcon />,
    },
    {
      title: 'Clientes',
      path: '/clientes',
      icon: <PeopleIcon />,
    },
    {
      title: 'Inventario',
      path: '/inventario',
      icon: <DevicesIcon />,
    },
    {
      title: 'Finanzas',
      path: '/finanzas',
      icon: <WalletIcon />,
    },
    {
      title: 'Redes Sociales',
      path: '/redes',
      icon: <CampaignIcon />,
    },
    {
      title: 'Gestión de Técnicos',
      path: '/technicians',
      icon: <TechnicianIcon />,
    },
    {
      title: 'Calendario',
      path: '/calendar',
      icon: <ScheduleIcon />,
    },
    {
      title: 'Mapa en Vivo',
      path: '/live-map',
      icon: <MapIcon />,
    },
    {
      title: 'Monitoreo',
      path: '/monitoreo',
      icon: <MonitorIcon />,
    },
  ];

  const botMenuItems: MenuItem[] = [
    {
      title: 'CRM Chat',
      path: '/bot/leads',
      icon: <WhatsAppIcon />,
    },
    {
      title: 'Leads',
      path: '/bot/leads-board',
      icon: <LeadsIcon />,
    },
    {
      title: 'Rutas de Instalación',
      path: '/bot/routes',
      icon: <RouteIcon />,
    },
    {
      title: 'Base de Conocimiento',
      path: '/bot/knowledge',
      icon: <KnowledgeIcon />,
    },
    {
      title: 'Inteligencia del Bot',
      path: '/bot/insights',
      icon: <InsightsIcon />,
    },
  ];

  // Sistema (lo demás se consolidó en los hubs Clientes / Inventario / Finanzas)
  const adminMenuItems: MenuItem[] = [
    {
      title: 'Auditoría Worker',
      path: '/admin/audit',
      icon: <SecurityIcon />,
      adminOnly: true,
    },
  ];

  const handleItemClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const isSelected = (path: string) => {
    if (path === '/admin/dashboard' || path === '/assistant/dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const currentWidth = collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  const renderMenuItem = (item: MenuItem) => (
    <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
      <Tooltip title={collapsed ? item.title : ''} placement="right" arrow>
        <ListItemButton
          onClick={() => handleItemClick(item.path)}
          selected={isSelected(item.path)}
          sx={{
            borderRadius: 2,
            mx: collapsed ? 0.5 : 1,
            minHeight: 48,
            justifyContent: collapsed ? 'center' : 'initial',
            px: collapsed ? 2 : 2.5,
            '&.Mui-selected': {
              backgroundColor: 'primary.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
              '& .MuiListItemIcon-root': {
                color: 'white',
              },
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: collapsed ? 0 : 40,
              mr: collapsed ? 0 : 2,
              justifyContent: 'center',
            }}
          >
            {item.icon}
          </ListItemIcon>
          {!collapsed && (
            <>
              <ListItemText
                primary={item.title}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: isSelected(item.path) ? 600 : 400,
                  noWrap: true,
                }}
              />
              {item.badge && (
                <Chip
                  label={item.badge === 'pending' ? '!' : item.badge}
                  size="small"
                  color={item.badge === 'pending' ? 'warning' : 'primary'}
                  sx={{ height: 20, fontSize: '0.75rem' }}
                />
              )}
            </>
          )}
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo and Brand */}
      <Box sx={{ p: collapsed ? 1.5 : 3, textAlign: 'center', pt: { xs: 8, md: collapsed ? 1.5 : 3 } }}>
        <Box
          sx={{
            width: collapsed ? 40 : 60,
            height: collapsed ? 40 : 60,
            backgroundColor: 'primary.main',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto',
            marginBottom: collapsed ? 1 : 2,
            color: 'white',
            fontSize: collapsed ? '16px' : '24px',
            fontWeight: 'bold',
            transition: theme.transitions.create(['width', 'height', 'font-size'], {
              duration: theme.transitions.duration.shorter,
            }),
          }}
        >
          DP
        </Box>
        {!collapsed && (
          <>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Driports
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Portal GPS
            </Typography>
          </>
        )}
      </Box>

      <Divider />

      {/* Main Menu */}
      <List sx={{ flexGrow: 1, px: collapsed ? 0.5 : 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {isTechnician ? (
          technicianMenuItems.map(renderMenuItem)
        ) : isAdmin ? (
          <>
            {adminMainItems.map(renderMenuItem)}
            <Divider sx={{ my: 2 }} />
            {!collapsed && (
              <Typography variant="caption" sx={{ px: 2, py: 0.5, color: 'text.disabled', display: 'block' }}>
                SOFIA BOT / CRM
              </Typography>
            )}
            {botMenuItems.map(renderMenuItem)}
            <Divider sx={{ my: 2 }} />
            {!collapsed && (
              <Typography variant="caption" sx={{ px: 2, py: 0.5, color: 'text.disabled', display: 'block' }}>
                SISTEMA
              </Typography>
            )}
            {adminMenuItems.map(renderMenuItem)}
          </>
        ) : (
          // Asistente: menú actual sin cambios
          menuItems.map(renderMenuItem)
        )}
      </List>

      {/* Collapse Toggle Button (only on desktop) */}
      {!isMobile && (
        <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
          <Tooltip title={collapsed ? 'Expandir menú' : 'Colapsar menú'} placement="right">
            <IconButton
              onClick={onToggleCollapse}
              sx={{
                backgroundColor: 'action.hover',
                '&:hover': {
                  backgroundColor: 'action.selected',
                },
              }}
            >
              {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* User Role Indicator */}
      <Box sx={{ p: collapsed ? 1 : 2 }}>
        {collapsed ? (
          <Tooltip title={isAdmin ? 'Administrador' : isTechnician ? 'Técnico' : 'Asistente'} placement="right">
            <Chip
              label={isAdmin ? 'A' : isTechnician ? 'T' : 'As'}
              color={isAdmin ? 'primary' : isTechnician ? 'success' : 'secondary'}
              size="small"
              sx={{ width: '100%', minWidth: 0 }}
            />
          </Tooltip>
        ) : (
          <Chip
            label={isAdmin ? 'Administrador' : isTechnician ? 'Técnico' : 'Asistente'}
            color={isAdmin ? 'primary' : isTechnician ? 'success' : 'secondary'}
            variant="outlined"
            size="small"
            sx={{ width: '100%' }}
          />
        )}
      </Box>
    </Box>
  );

  // En móviles, usar drawer temporal
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        anchor="left"
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Mejor rendimiento en móviles
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawer}
      </Drawer>
    );
  }

  // En desktop, usar drawer persistente con opción de colapsar
  return (
    <Drawer
      variant="permanent"
      anchor="left"
      sx={{
        width: currentWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: currentWidth,
          boxSizing: 'border-box',
          borderRight: '1px solid rgba(0, 0, 0, 0.05)',
          position: 'fixed',
          height: '100vh',
          zIndex: theme.zIndex.drawer,
          overflowX: 'hidden',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        },
      }}
    >
      {drawer}
    </Drawer>
  );
};

export default Sidebar;
